import type { CatalogueOption, CatalogueRef, ProviderAttestation, ProviderEmailConfig, ProviderEmailPreview } from '../../api/emailCatalogue';
export type NodeKind =
  | "link_claim"
  | "record_note"
  | "update_claim"
  | "complete_task"
  | "archive_task"
  | "refer_staff"
  | "send_email"
  | "create_follow_up"
  | "wait"
  | "condition"
  | "loa_portal"
  | "multi_rep"
  | "cancel_confirmation"
  | "forward_correspondence"
  | "cancel_claims"
  | "project_intake_assessment"
  | "reconcile_claim_follow_ups"
  | "finish_intake_task"
  | "ingest_claim_correspondence"
  | "stop";
export interface TokenMapping {
  source: string;
  type: "string" | "date" | "list";
  format: string;
  node_id?: string;
  output?: string;
  slot?: string;
  slot_type?: string;
  input_format?: string;
  source_name?: string;
  partition?: string;
  default?: string | unknown[];
}
export type ProtectedEmail = {
  template_id: string;
  recipient: "client";
  sender_identity:
    | "claims_team"
    | "loa_team"
    | "closed_claim_forward_team";
  subject: string;
  tokens: Record<string, TokenMapping>;
  attachments: Array<"source_email" | "source_attachments">;
};
export type ProtectedFollowUp = {
  title: string;
  instructions: string;
  team: "caf_staff_shared_review";
  due_after_seconds: number;
};
export type ProtectedChaser = {
  email: ProtectedEmail | ProviderEmailConfig;
  response_policy: {
    policy_version: 2;
    scope: "matched_claim";
    source_ids: ["client_email", "staff_recorded_response"];
    start_from: "provider_acceptance";
    timeout_seconds: number;
    stop_on: "any_attributable_response";
  };
};
export type ProtectedConfigV2 = {
  config_version: 2;
  operation: string;
  reminder_ownership: "builtin" | "workflow";
  email: ProtectedEmail | ProviderEmailConfig;
  follow_up: ProtectedFollowUp | null;
  chaser: ProtectedChaser | null;
  forwarding_policy?: { version: 1; mode: "received_email"; claim_scope: "active_and_cancelled" };
};
export type ProtectedKind =
  | "loa_portal"
  | "multi_rep"
  | "cancel_confirmation"
  | "forward_correspondence";
export const PROTECTED_KINDS: ProtectedKind[] = [
  "loa_portal",
  "multi_rep",
  "cancel_confirmation",
  "forward_correspondence",
];
export const isProtectedKind = (kind: NodeKind): kind is ProtectedKind =>
  (PROTECTED_KINDS as NodeKind[]).includes(kind);
/**
 * Draft form of a protected email. A configured ID the server could not
 * capture stays null: it is a visible blocker, never an invented default.
 */
export type LegacyProtectedEmailDraft = Omit<
  ProtectedEmail,
  "template_id" | "sender_identity"
> & {
  template_id: string | null;
  sender_identity: ProtectedEmail["sender_identity"] | null;
};
export type ProtectedEmailDraft = LegacyProtectedEmailDraft | ProviderEmailConfig;
export type ProtectedChaserDraft = Omit<ProtectedChaser, "email"> & {
  email: ProtectedEmailDraft;
};
export type ProtectedConfigDraft = Omit<ProtectedConfigV2, "email" | "chaser"> & {
  email: ProtectedEmailDraft;
  chaser: ProtectedChaserDraft | null;
};
export interface ProtectedOutputContract {
  type: "string";
  formats: string[];
  label: string;
}
export interface BusinessUpgradeChange {
  node_id: string;
  before: NodeConfig;
  after: NodeConfig;
}
export interface BusinessUpgradeBlocker {
  node_id: string;
  field: string;
  code: string;
}
export interface BusinessUpgradePreview {
  definition: WorkflowDefinition;
  changes: BusinessUpgradeChange[];
  blockers: BusinessUpgradeBlocker[];
}
export interface OutputContract {
  type: TokenMapping["type"];
  formats: string[];
  default_allowed: boolean;
}
export interface PatternInputContract extends OutputContract {
  slot_type: string;
  input_formats: string[];
  partitions: string[];
  required: boolean;
}
export interface TokenSource extends OutputContract {
  source: string;
  description: string;
  required: boolean;
  source_metadata: { scope: string; field: string };
  item_schema?: {
    type: string;
    required: string[];
    properties: Record<string, { type: string }>;
  };
}
export interface Due {
  mode: "relative" | "until";
  seconds?: number;
  at?: string;
}
/** Optional fields represent an in-progress form; the server validates complete canonical nodes. */
export interface NodeConfig {
  delivery_mode?: 'provider_template_v1';
  catalogue_ref?: CatalogueRef;
  catalogue_emails?: Record<string, CatalogueRef>;
  config_version?: 1 | 2;
  entry_type?: string;
  text?: string;
  updates?: Array<{ field: "vehicle_registration"; value: string | null }>;
  /** Free text on ordinary blocks; the pinned assessment source on intake archive v2. */
  reason?: string | ArchiveReasonSource;
  team?: string;
  template_id?: string;
  recipient?: string;
  subject?: string;
  tokens?: Record<string, TokenMapping>;
  attachments?: string[];
  sender_identity?: string;
  reminder_for_node_id?: string;
  title?: string;
  instructions?: string;
  assignee?: string;
  due?: Due;
  mode?: string;
  seconds?: number;
  at?: string;
  send_node_id?: string;
  timeout_seconds?: number;
  scope?: string;
  source_ids?: string[];
  start_from?: "provider_acceptance";
  request_output?: { node_id: string; output: "request_id" };
  request_id?: string;
  fact?: string;
  operator?: string;
  value?: string | boolean;
  field?: string;
  document_type?: "loa_submission";
  request_type?: "loa_questionnaire";
  operation?: string;
  reminder_ownership?: "builtin" | "workflow";
  email?: ProtectedEmailDraft;
  follow_up?: ProtectedFollowUp | null;
  chaser?: ProtectedChaserDraft | null;
  fee_mode?: "chargeable" | "non_chargeable";
  reason_details?: string;
  subset?: { predicate: "claim_status"; value: "active" | "closed" };
  non_cooperation_reason_codes?: string[];
  outcome?: "completed" | "review" | IntakeOutcome;
  /** Intake parity (Task 7): closed shapes mirrored from the backend validators. */
  projection?: "caf_exact_nonstaff" | "caf_intake_v1";
  policy?: "caf_response_v1";
  requires?: FinishRequires;
  profile?: IngestionProfileV1;
  /** R2 option (a): reviewed fact on manual review / archive v2 / finish blocks. */
  final_response_fact?: FinalResponseFact | null;
}
export type IntakeAction =
  | "AUTO_ARCHIVE"
  | "MANUAL_REVIEW"
  | "AUTO_ASSIGN_ONLY"
  | "AUTO_ASSIGN_COMPLETE"
  | "AUTO_ASSIGN_SEND_LOA"
  | "AUTO_ASSIGN_MULTI_REP"
  | "AUTO_ASSIGN_CANCEL_CONFIRM";
export type IntakeOutcome = Exclude<IntakeAction, "AUTO_ARCHIVE">;
export interface ArchiveReasonSource {
  source: "approved_intake_assessment";
}
export interface FinishRequires {
  ingestion_node: string | null;
  reconciliation_node: string | null;
  outbound_node: string | null;
}
export type IntakeGuardFact = "claim.identity_present" | "claim.cancellation_state";
export const INTAKE_GUARD_CONFIGS: Record<IntakeGuardFact, NodeConfig> = {
  "claim.identity_present": {
    fact: "claim.identity_present",
    operator: "equals",
    value: true,
  },
  "claim.cancellation_state": {
    fact: "claim.cancellation_state",
    operator: "equals",
    value: "cancelled",
  },
};
export const isIntakeGuardFact = (fact: unknown): fact is IntakeGuardFact =>
  fact === "claim.identity_present" || fact === "claim.cancellation_state";
/** CafIngestionProfileV1 mapping value: exactly one of the three closed sources. */
export type ProfileMappingValue =
  | { source: "approved_literal"; value: string | number; evidence_ref: string }
  | {
      source: "approved_pattern_slot";
      mapping: {
        source: "pattern_slot";
        slot: string;
        slot_type: string;
        input_format: string;
        source_name: string;
        partition: "current" | "quoted";
        type: string;
        format: string;
      };
    }
  | { source: "trusted_received_date" };
export interface ProfileMapping {
  field_id: string;
  target_claim_field: string;
  data_type: string;
  value: ProfileMappingValue;
  disclosure_fact_ref: string | null;
}
export interface FinalResponseFact {
  version: 1;
  value: boolean;
  evidence_ref: string;
}
export interface IngestionProfileV1 {
  version: 1;
  category: string;
  category_origin: {
    reviewed_entry_id: string;
    reviewed_version: number;
    source_hash: string;
  };
  secondary_commission_fact: null | {
    value: "DCA" | "Non-DCA";
    evidence_ref: string;
  };
  /**
   * Absent keeps a historical profile's hash; null records "no reusable fact";
   * a value is a reviewed, versioned fact about this reviewed source only.
   */
  final_response_fact?: FinalResponseFact | null;
  extraction: {
    enabled: boolean;
    schema_hash: string | null;
    corpus_entry_id: string | null;
    mappings: ProfileMapping[];
  };
  policy_version: "caf_ingestion_v1";
}
export type ReadinessCode =
  | "ready"
  | "missing_recognition"
  | "missing_identity"
  | "verified_input_gap"
  | "missing_lender_source_evidence"
  | "missing_settings"
  | "source_review_required"
  | "workflow_paused";
/** Minimal JSON Schema subset the parity inspector publishes. */
export interface InspectorSchema {
  type?: string | string[];
  enum?: Array<string | number | boolean | null>;
  const?: unknown;
  properties?: Record<string, InspectorSchema>;
  required?: string[];
  oneOf?: InspectorSchema[];
  items?: InspectorSchema;
  maxItems?: number;
  $ref?: string;
  $defs?: Record<string, InspectorSchema>;
  "x-backend-rules"?: string[];
  [key: string]: unknown;
}
export interface IntakeConditionEntry {
  fact: IntakeGuardFact;
  operators: string[];
  values: Array<string | boolean>;
  ports: string[];
  unknown: string;
  evidence: string[];
  label: string;
  branch_labels: Record<"true" | "false" | "unknown", string>;
}
type FinishShape = {
  ingestion_node: NodeKind | null;
  reconciliation_node: NodeKind | null;
  outbound_node: NodeKind | null;
};
export interface ParityInspectorCatalogue {
  schema_version: 1;
  intake_actions: IntakeAction[];
  canonical_sequences: Partial<Record<IntakeAction, string[]>>;
  readiness_codes: ReadinessCode[];
  recognition_states: string[];
  profile_schema: InspectorSchema;
  node_config_schemas: Record<string, InspectorSchema>;
  intake_conditions: IntakeConditionEntry[];
  /** Block kinds whose own config may carry the reviewed final-response fact. */
  final_response_fact_node_kinds?: NodeKind[];
  profile_fields: {
    category: { editable: boolean; provenance: string };
    category_origin: { editable: false; reason: string };
    secondary_commission_fact: {
      editable: boolean;
      values: Array<"DCA" | "Non-DCA" | null>;
    };
    final_response_fact: {
      editable: boolean;
      values: Array<boolean | null>;
      absent_means: string;
      requires_source_review: boolean;
    };
    extraction: { editable: boolean; mapping_sources: ProfileMappingValue["source"][] };
    policy_version: { editable: false };
  };
  finish_prerequisites: Partial<
    Record<IntakeOutcome, FinishShape | FinishShape[]>
  >;
  reconciliation: { policy: "caf_response_v1"; summary: string };
  archive_reason_sources: Array<{ value: string; label: string }>;
  task_outcomes: Partial<Record<IntakeOutcome, string>>;
  read_only_restrictions: string[];
  run_history: {
    dispositions: Array<"accepted" | "authorized_no_send">;
    no_send_reasons: Partial<Record<NodeKind, string[]>>;
    hidden_fields: string[];
  };
}
/** Server-derived per-node publication record (Task 6); read-only evidence. */
export interface IntakeCapabilityRecord {
  node_id: string;
  ingestion_profile_hash: string | null;
  target_requirement?: string;
  identity_guard?: { condition_node_id?: string; present?: boolean } | null;
  branch_guard?: { condition_node_id: string; state: string } | null;
  expected_action?: string;
  [key: string]: unknown;
}
export interface ProfileHashRef {
  node_id: string;
  profile_hash: string;
}
export interface CanonicalDefinition {
  definition_key: string;
  behavior_hash: string;
  graph_hash: string;
  profile_hashes: ProfileHashRef[];
  definition: WorkflowDefinition;
  reuse_pin: null | { workflow_id: string; workflow_version: number };
}
export interface CanonicalMapping {
  entry_id: string;
  source_sender: string;
  reviewed_version: number;
  source_hash: string;
  definition_key: string;
  behavior_hash: string;
  selected_profile_hashes: ProfileHashRef[];
  reviewed_origin_hash: string;
  eligibility: {
    proof_candidate: unknown;
    selected_profiles: unknown[];
    review_hash: string;
    gaps: Array<{ code: string; node_id: string | null; path: string; kind: string }>;
    source_display: null;
  };
  executable_readiness: ReadinessCode;
}
export interface CanonicalException {
  entry_id: string;
  source_sender: string;
  code: string;
  field: string | null;
  reviewed_origin_hash: string | null;
}
export interface CanonicalPreviewResult {
  definitions: CanonicalDefinition[];
  mappings: CanonicalMapping[];
  exceptions: CanonicalException[];
}
export interface WorkflowNode {
  id: string;
  kind: NodeKind;
  config: NodeConfig;
}
export interface WorkflowEdge {
  source: string;
  port: string;
  target: string;
}
export interface WorkflowDefinition {
  schema_version: 1;
  name: string;
  entry: string;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
}
export interface WorkflowRecord {
  workflow_id: string;
  revision: number;
  current_version: number | null;
  active_version: number | null;
  active: boolean;
  name: string;
  definition?: WorkflowDefinition;
  definition_hash?: string;
  updated_at?: string;
}
export interface WorkflowVersion {
  workflow_id: string;
  version: number;
  definition: WorkflowDefinition;
  source_revision: number;
  published_at: string;
  reason: string;
  published_by: { id: string; email: string };
  definition_hash?: string;
  server_validation: {
    catalogue_templates?: Record<string, ProviderAttestation>;
    intake_capabilities?: Record<string, IntakeCapabilityRecord>;
    templates?: Record<
      string,
      { template_id: string; version: number; source_hash: string }
    >;
  };
}
export interface Page<T> {
  items: T[];
  next_cursor: string | null;
}
export interface HistoryEvent {
  event_id: string;
  kind: string;
  version: number | null;
  revision: number;
  reason: string | null;
  created_at: string;
  actor: { id: string; email: string };
}
export interface TemplateSummary {
  template_id: string;
  version: number;
  source_hash: string;
  tokens: string[];
}
export interface TemplateSource extends TemplateSummary {
  current_version: number;
  html: string;
  text: string | null;
  renderer_version: number;
  provider_metadata: Record<string, unknown> | null;
  reapproval_required: boolean;
  content_authority: "reviewed_local_import";
  remote_content_verified: false;
}
export interface SourceStatus {
  template_id: string;
  status: string;
  reason_code?: string;
  version?: number;
  source_hash?: string;
  variables?: string[];
  tokens?: string[];
}
export interface CompoundCapability {
  recipient: string;
  template: SourceStatus;
  variables: string[];
  reminder_ownership: string[];
  effects: string[];
  builtin_chaser_configured?: boolean;
  response_policy?: Record<string, unknown>;
  reminder_template?: SourceStatus;
}
export interface CancellationReason {
  roles?: Array<{ role: string; purpose: string; relationship: string; recipient: string }>;
  value: string;
  bypasses_payment: boolean;
  requires_non_cooperation_codes: boolean;
  templates: SourceStatus[];
}
export interface AuthoringCatalogue {
  catalogue_options?: CatalogueOption[];
  catalogue_next_cursor?: string | null;
  catalogue_blockers?: string[];
  schema_version: 1;
  nodes: Array<{ kind: NodeKind; label: string }>;
  node_outputs: Partial<Record<NodeKind, Record<string, OutputContract>>>;
  tokens: TokenSource[];
  pattern_inputs?: PatternInputContract[];
  limits: {
    nodes: number;
    edges: number;
    min_wait_seconds: number;
    max_wait_seconds: number;
  };
  assignments: {
    queue: { value: string; label: string };
    members: Array<{ value: string; label: string; available: boolean }>;
    status: string;
  };
  compounds: Partial<Record<NodeKind, CompoundCapability>>;
  cancellation: {
    reasons: CancellationReason[];
    non_cooperation_reasons: Record<string, string>;
  };
  recipients: string[];
  sender_identities: string[];
  attachments: string[];
  response_sources: string[];
  /** Read-only parity inspector schemas and labels (Task 7a contract). */
  parity_inspector?: ParityInspectorCatalogue;
  /** Captured once per catalogue read; copied into a draft only when a block is added. */
  business_defaults?: Partial<Record<ProtectedKind, ProtectedConfigDraft>>;
  business_reminder_defaults?: { multi_rep?: ProtectedChaserDraft };
  protected_outputs?: Partial<
    Record<
      ProtectedKind,
      {
        initial: Record<string, ProtectedOutputContract>;
        reminder: Record<string, ProtectedOutputContract> | null;
      }
    >
  >;
  /** Only identities the server can currently verify, named without addresses. */
  business_senders?: Partial<
    Record<
      ProtectedKind,
      Array<{ value: ProtectedEmail["sender_identity"]; label: string }>
    >
  >;
}
export type PreviewMessage = LegacyPreviewMessage | ProviderEmailPreview;
export interface LegacyPreviewMessage extends TemplateSummary {
  delivery_mode?: undefined;
  rendered: { html: string; text: string; subject: string };
  recipient: string;
  token_values: Record<string, unknown>;
  token_provenance?: Record<
    string,
    {
      origin:
        | "catalogue"
        | "node_output"
        | "business_placeholder"
        | "pattern_slot"
        | "protected_output";
      mapping?: TokenMapping;
      source_metadata?: Record<string, string>;
      owner?: string;
      variable?: string;
      type?: string;
      format?: string;
      synthetic: true;
    }
  >;
  placeholder_outputs: string[];
  provider_metadata: Record<string, unknown> | null;
  content_authority: string;
  remote_content_verified: false;
}
export interface PreviewNode {
  synthetic_claim_ids?: string[];
  synthetic_claim_examples?: Array<{ id: string; status: string }>;
  node_id: string;
  kind: NodeKind;
  status: string;
  messages: PreviewMessage[];
  reason_code?: string;
  message?: string;
  attachments?: string[];
  outputs?: Record<
    string,
    { placeholder: boolean; protected_output?: string; type?: string }
  >;
  target_policy?: string;
  fee_policy?: string;
  reminder_ownership?: "builtin" | "workflow";
  builtin_chaser_configured?: boolean;
  response_policy?: { timeout_seconds: number };
}
export interface WorkflowPreviewResult {
  valid: true;
  definition: WorkflowDefinition;
  synthetic: true;
  effects_performed: false;
  nodes: PreviewNode[];
  notice: string;
}
export interface ApiProblem {
  message: string;
  path?: string;
  code?: string;
  conflict: boolean;
}
export interface PatternDefinition {
  schema_version: 1;
  pattern_id: string;
  pattern_version: number;
  workflow_id: string;
  workflow_version: number;
  sender: string;
  meaning: string;
  sources: Record<
    string,
    { kind: "body" | "subject" | "attachment"; signature_literals?: string[] }
  >;
  required: Array<{ source: string; text: string }>;
  forbidden: Array<{ source: string; text: string }>;
  templates: Record<string, Array<{ literal: string } | { slot: string }>>;
  slots: Record<string, { type: string; format?: string }>;
  attachment_policy: { approved_inline_sha256: string[] };
  quoted_templates?: Record<
    string,
    Array<{ literal: string } | { slot: string }>
  >;
  positive_examples?: string[];
  negative_examples?: string[];
  notes?: string;
}
export interface PatternRecord {
  id: string;
  sender: string;
  version: number;
  workflow_pattern: {
    state: "needs-review" | "approved" | "paused";
    definition: PatternDefinition;
    definition_hash: string;
  } | null;
  publication_status: string;
  pattern_operation_id: string | null;
  legacy_summary?: {
    sample_text: string;
    correct_action: string;
    note: string;
    source: string;
    truncated: boolean;
    source_complete: false;
  };
}
export interface AffectedPattern {
  entry_id: string;
  source_sender: string;
  source_version: number;
  state: string;
  pattern_version: number;
  workflow_version: number;
  pending_operation: string | null;
}
export interface PatternExample {
  label: string;
  sender: string;
  sources: Record<
    string,
    string | { current: string; quoted?: string; signature?: string }
  >;
}
export interface PatternPreviewResult {
  synthetic: true;
  grants_authority: false;
  synthetic_names: string[];
  examples: Array<{
    label: string;
    result: {
      status: string;
      reason_code: string;
      extracted: Record<string, unknown>;
    };
  }>;
  source_eligibility?: {
    review_hash: string;
    scope: "guidance" | "executable";
    behavior_hash: string;
    workflow_pin: {
      workflow_id: string;
      workflow_version: number;
      workflow_hash: string;
    };
    profile_hashes: Array<{ node_id: string; profile_hash: string }>;
    selected_profile_hashes: Array<{ node_id: string; profile_hash: string }>;
    pattern_descriptor: null | {
      pattern_id: string;
      pattern_version: number;
      definition_hash: string;
      matcher_version: number;
    };
    gaps: Array<{ code: string; node_id: string | null; path: string; kind: string }>;
    selected_origin: {
      entry_id: string;
      source_sender: string;
      reviewed_version: number;
      reviewed_source_hash: string;
    };
    obligations: Array<{
      node_id: string;
      path: string;
      kind: string;
      value_hash: string;
      evidence_refs: string[];
      typed_value: unknown;
    }>;
    review_items: Array<{
      node_id: string;
      path: string;
      kind: string;
      typed_value: unknown;
      evidence_refs: string[];
      status: "proved" | "reviewable" | "unresolvable";
      reason: string;
      item_hash: string;
    }>;
    evidence: Array<{
      id: string;
      kind: string;
      source_hash: string;
      facts: Array<Record<string, unknown>>;
      details: Record<string, unknown>;
    }>;
    source_display: null | { status: string; reason_code: string } | Array<{
      source_id: string;
      name: string;
      text: string;
      text_hash: string;
      readable: boolean;
      complete: boolean;
      parts: Array<Record<string, unknown>>;
    }>;
    evidence_selections: Array<{
      node_id: string;
      path: string;
      evidence_refs: string[];
    }>;
    invariant_clauses: {
      required: unknown[];
      forbidden: unknown[];
      templates: Record<string, unknown>;
      quoted_templates: Record<string, unknown>;
    };
  };
}

export const LABELS: Record<NodeKind, string> = {
  link_claim: "Link verified claim",
  record_note: "Record claim activity",
  update_claim: "Update vehicle registration",
  complete_task: "Complete source task",
  archive_task: "Archive source task",
  refer_staff: "Refer source task to staff",
  send_email: "Send template email",
  create_follow_up: "Create staff follow-up",
  wait: "Wait",
  condition: "Check a condition",
  loa_portal: "Request LOA through secure portal",
  multi_rep: "Send multi-representative notice",
  cancel_confirmation: "Reaffirm authority",
  forward_correspondence: "Forward closed-claim correspondence to client",
  cancel_claims: "Follow cancellation process",
  project_intake_assessment: "Project intake assessment for manual review",
  reconcile_claim_follow_ups: "Reconcile claim follow-ups",
  finish_intake_task: "Finish source intake task",
  ingest_claim_correspondence: "Ingest claim correspondence",
  stop: "Stop",
};
export const portLabel = (port: string) =>
  ({
    success: "Success",
    true: "Yes",
    false: "No",
    unknown: "Unknown — hold",
    responded: "Client responded — stop for review",
    timeout: "No response by deadline",
  })[port] || port;
export const portsFor = (node: WorkflowNode): string[] =>
  node.kind === "stop"
    ? []
    : node.kind === "condition"
      ? ["true", "false", "unknown"]
      : node.kind === "wait" && node.config.mode === "client_response"
        ? ["responded", "timeout"]
        : ["success"];
/**
 * Port label for one block. Intake guards use their catalogue branch names
 * (never Yes/No, never the historical active/closed wording).
 */
export function portLabelFor(
  node: WorkflowNode | undefined,
  port: string,
  catalogue?: AuthoringCatalogue | null,
): string {
  if (node?.kind === "condition" && isIntakeGuardFact(node.config.fact)) {
    const entry = catalogue?.parity_inspector?.intake_conditions.find(
      (item) => item.fact === node.config.fact,
    );
    const label = entry?.branch_labels[port as "true" | "false" | "unknown"];
    if (label) return label;
  }
  return portLabel(port);
}
/**
 * Nodes on every path from the entry to ``id`` (its dominators), in graph
 * order. Required predecessors can only be chosen from these; the server still
 * validates every choice.
 */
export function dominators(
  definition: WorkflowDefinition,
  id: string,
): WorkflowNode[] {
  const reachable = (blocked: string | null) => {
    const seen = new Set<string>();
    const stack = definition.entry === blocked ? [] : [definition.entry];
    while (stack.length) {
      const current = stack.pop()!;
      if (seen.has(current)) continue;
      seen.add(current);
      definition.edges
        .filter((edge) => edge.source === current && edge.target !== blocked)
        .forEach((edge) => stack.push(edge.target));
    }
    return seen;
  };
  if (!reachable(null).has(id)) return [];
  return definition.nodes.filter(
    (node) => node.id !== id && !reachable(node.id).has(id),
  );
}
export function ancestors(
  definition: WorkflowDefinition,
  id: string,
): WorkflowNode[] {
  const found = new Set<string>();
  const visit = (target: string) =>
    definition.edges
      .filter((edge) => edge.target === target)
      .forEach((edge) => {
        if (edge.source !== id && !found.has(edge.source)) {
          found.add(edge.source);
          visit(edge.source);
        }
      });
  visit(id);
  return definition.nodes.filter((node) => found.has(node.id));
}
export function defaultConfig(kind: NodeKind): NodeConfig {
  switch (kind) {
    case "link_claim":
    case "complete_task":
      return {};
    case "record_note":
      return { entry_type: "note", text: "" };
    case "update_claim":
      return { updates: [{ field: "vehicle_registration", value: "" }] };
    case "archive_task":
      return { reason: "" };
    case "refer_staff":
      return { team: "caf_staff_shared_review", reason: "" };
    case "send_email":
      return {
        template_id: "",
        recipient: "client",
        subject: "",
        tokens: {},
        attachments: [],
        sender_identity: "claims_team",
      };
    case "create_follow_up":
      return {
        title: "",
        instructions: "",
        team: "caf_staff_shared_review",
        due: { mode: "relative", seconds: 86400 },
      };
    case "wait":
      return { mode: "duration", seconds: 86400 };
    case "condition":
      return { fact: "claim.status", operator: "equals", value: "active" };
    case "loa_portal":
      return {
        operation: "send_loa_portal_link",
        reminder_ownership: "builtin",
      };
    case "multi_rep":
      return {
        operation: "send_multi_rep_notice",
        reminder_ownership: "builtin",
      };
    case "cancel_confirmation":
      return {
        operation: "send_cancel_confirmation",
        reminder_ownership: "builtin",
      };
    case "forward_correspondence":
      return {
        operation: "forward_source_correspondence",
        reminder_ownership: "builtin",
        recipient: "client",
      };
    case "cancel_claims":
      return {
        scope: "matched_claim",
        reason: "",
        fee_mode: "non_chargeable",
        reason_details: "",
      };
    // Intake parity blocks are never added from the palette; these are their
    // closed canonical shapes so a hand-built draft starts valid-looking.
    case "project_intake_assessment":
      return { config_version: 1, outcome: "MANUAL_REVIEW" };
    case "reconcile_claim_follow_ups":
      return { config_version: 1, policy: "caf_response_v1" };
    case "finish_intake_task":
      return {
        config_version: 1,
        outcome: "AUTO_ASSIGN_ONLY",
        requires: { ingestion_node: null, reconciliation_node: null, outbound_node: null },
      };
    case "ingest_claim_correspondence":
      return { config_version: 1 };
    case "stop":
      return { outcome: "completed" };
  }
}
export const DURATION_UNITS = [
  { value: 86400, singular: "day", plural: "days" },
  { value: 3600, singular: "hour", plural: "hours" },
  { value: 60, singular: "minute", plural: "minutes" },
];
/** Largest whole unit for an exact integer number of seconds. */
export function durationParts(seconds: number) {
  const unit =
    DURATION_UNITS.find((item) => seconds > 0 && seconds % item.value === 0) ||
    DURATION_UNITS[DURATION_UNITS.length - 1];
  return { amount: seconds / unit.value, unit };
}
export function formatDuration(seconds: number): string {
  if (!Number.isInteger(seconds) || seconds % 60 !== 0)
    return `${seconds} seconds`;
  const { amount, unit } = durationParts(seconds);
  return `${amount} ${amount === 1 ? unit.singular : unit.plural}`;
}
export const MIN_BUSINESS_DELAY = 60;
export const MAX_BUSINESS_DELAY = 31536000;

/* Workflow navigation (read-only; grants no recognition or binding authority). */
export interface WorkflowUse {
  entry: { sender: string; id: string };
  usage_kind: "guidance_reference" | "pattern" | "both";
  versions: Array<{
    workflow_id: string;
    workflow_version: number;
    name: string | null;
    catalogue_status: "active" | "published" | "missing";
    editor_url: string | null;
  }>;
  recognition_state: string;
  entry_state: string;
  current: boolean;
  authority: "navigation_only";
}
export interface WorkflowUsePage extends Page<WorkflowUse> {
  counts: {
    confirmed: number | null;
    unavailable_in_scope: number | null;
    unknown_scope: number | null;
  };
}
/** Built-in (canonical) workflow definitions are persisted under this prefix. */
export const CANONICAL_WORKFLOW_PREFIX = "caf-canonical-";
export const isCanonicalWorkflowId = (id: string) =>
  id.startsWith(CANONICAL_WORKFLOW_PREFIX);
/** Truthful catalogue label: never call a never-activated workflow "paused". */
export const workflowCatalogueStatus = (item: WorkflowRecord) =>
  item.active
    ? "active"
    : item.active_version
      ? "paused"
      : item.current_version
        ? "published, not active"
        : "draft only";
/** The only page path navigation links use (the existing AI curation page). */
export const WORKFLOW_EDITOR_PATH = "/ai-curation";
export const workflowEditorLink = (workflowId: string, version?: number) =>
  `${WORKFLOW_EDITOR_PATH}?workflowId=${encodeURIComponent(workflowId)}${
    version !== undefined ? `&workflowVersion=${version}` : ""
  }`;
export type WorkflowLinkTarget =
  | { kind: "none" }
  | { kind: "workflow"; workflowId: string; version?: number }
  | { kind: "invalid"; message: string };
/** Parse `workflowId`/`workflowVersion` deep-link parameters strictly. */
export function parseWorkflowLink(search: string): WorkflowLinkTarget {
  const params = new URLSearchParams(search);
  const ids = params.getAll("workflowId"),
    versions = params.getAll("workflowVersion");
  if (!ids.length && !versions.length) return { kind: "none" };
  const workflowId = ids[0]?.trim() || "";
  if (ids.length !== 1 || !workflowId || workflowId.length > 128)
    return {
      kind: "invalid",
      message: "This workflow link has no valid workflow ID, so nothing was opened.",
    };
  if (!versions.length) return { kind: "workflow", workflowId };
  const raw = versions[0];
  if (versions.length !== 1 || !/^[1-9][0-9]{0,8}$/.test(raw))
    return {
      kind: "invalid",
      message:
        "This workflow link's version is not a positive whole number, so nothing was opened.",
    };
  return { kind: "workflow", workflowId, version: Number(raw) };
}

/* ------------------------------------------------------------------ */
/* Guidance references and selected binding updates (guidance-bindings) */
/* Closed shapes mirror the server exactly; nothing here is authority.  */
/* ------------------------------------------------------------------ */

/** One exact immutable published workflow version. */
export interface WorkflowPin {
  workflow_id: string;
  workflow_version: number;
}
/** One explicitly selected source, with the CAS captured when it was chosen. */
export interface SelectionV1 {
  entry_id: string;
  source_sender: string;
  expected_version: number;
  reviewed_token?: string;
}
export interface WorkflowReferenceV1 extends WorkflowPin {
  schema_version: 1;
  attached_source_version: number;
  operation_id: string;
  actor: { id: string; email: string };
  reason: string;
  recorded_at: string;
}
export type GuidanceUsageKind = "guidance_reference" | "pattern" | "both" | null;
export interface GuidanceReferenceView {
  id: string;
  sender: string;
  version: number;
  compare_token: string;
  workflow_reference: WorkflowReferenceV1 | null;
  workflow_pattern_pin: WorkflowPin | null;
  usage_kind: GuidanceUsageKind;
  publication_status: string;
}
export type ReferenceMutationStatus = "complete" | "pending" | "conflict" | "held" | "noop";
/** Existing owned recovery routes and their actual role floors. */
export interface ReferenceRecoveryLink {
  kind: "navigation" | "pattern";
  operation_id: string;
  reconcile_path: string;
  required_permission: "draft";
  body: ["reason"];
  pattern_reconcile_path?: string;
  pattern_required_permission?: "activate";
}
export interface ReferenceMutation {
  operation_id: string | null;
  status: ReferenceMutationStatus;
  source_version: number | null;
  result: GuidanceReferenceView | null;
  reason_code: string | null;
  recovery: ReferenceRecoveryLink | null;
}
export interface EvidenceSelectionV1 {
  evidence_id: string;
  obligation_paths: string[];
}
export interface BindingReviewRequest {
  review_hash: string;
  evidence_selections: EvidenceSelectionV1[];
}
export interface BindingEligibility {
  proof_candidate: unknown;
  selected_profiles: unknown;
  review_hash: string;
  gaps: Array<{ code: string; path?: string; [key: string]: unknown }>;
}
export interface BindingSource {
  entry_id: string;
  source_sender: string;
  expected_version: number;
  etag: string;
  source_hash: string;
  reviewed_token: string | null;
}
export interface BindingTarget extends WorkflowPin {
  workflow_hash: string;
  control_revision: number | null;
  dependency_hash: string;
}
export type BindingItemAction = "guidance_reference" | "pattern_binding";
export interface BindingPreviewItem {
  ordinal: number;
  operation_id: string;
  source: BindingSource;
  parent_source: Record<string, unknown> | null;
  before_pin: WorkflowPin | null;
  action: BindingItemAction;
  pattern_snapshot: { state?: string; [key: string]: unknown } | null;
  assessment_snapshot: Record<string, unknown> | null;
  recognition_hash: string | null;
  eligibility: BindingEligibility | null;
  review_request: BindingReviewRequest | null;
  required_permission: "draft" | "publish";
  readiness: "ready" | "held";
  reason_codes: string[];
}
export interface BindingPreviewV1 {
  version: 1;
  batch_id: string;
  preparation_id: string;
  preparer: { id: string; email: string };
  reason: string;
  target: BindingTarget;
  items: BindingPreviewItem[];
  parent: null | Record<string, string>;
}
export type BindingItemState = "ready" | "applying" | "complete" | "pending" | "conflict" | "held";
export type BindingBatchState = "preview" | "applying" | "complete" | "partial" | "pending";
export type BindingConformance = "matches" | "applied_then_changed" | "pending" | "unavailable" | null;
export interface BindingResultItem {
  ordinal: number;
  entry_id: string;
  source_sender: string;
  operation_id: string;
  state: BindingItemState;
  source_version: number | null;
  reason_code: string | null;
  recovery: { kind: "navigation" | "pattern"; operation_id: string } | null;
  current_conformance: BindingConformance;
}
export interface BindingCounts {
  selected: number;
  complete: number;
  pending: number;
  held: number;
  conflict: number;
  unstarted: number;
}
export interface BatchResumeLink {
  resume_path: string;
  body: ["reason"];
  required_permission: "draft" | "publish";
}
export interface BindingResult {
  batch_id: string;
  preview_hash: string;
  state: BindingBatchState;
  items: BindingResultItem[];
  counts: BindingCounts;
  recovery?: BatchResumeLink;
}
export interface BindingBatch {
  batch_id: string;
  preview_hash: string;
  preview: BindingPreviewV1;
  result: BindingResult;
  recovery?: BatchResumeLink;
}
export interface EligibilityReview extends BindingReviewRequest {
  operation_id: string;
}
export const MAX_BINDING_SELECTIONS = 50;

/* Canonical applications (Canonical-application Task 4): exact server shapes. */
export const MAX_CANONICAL_SELECTIONS = 128;
export interface CanonicalSelection {
  entry_id: string;
  source_sender: string;
  expected_version: number;
  reviewed_token: string | null;
}
export type CanonicalItemState = "unstarted" | "pending" | "complete" | "held" | "conflict";
export type CanonicalApplicationState =
  | "prepared"
  | "applying"
  | "complete"
  | "partial"
  | "pending"
  | "superseded";
export interface CanonicalActor {
  id: string;
  email: string;
}
export interface CanonicalLinks {
  inspect: string;
  recover: string;
}
export interface CanonicalCounts {
  selected: number;
  complete: number;
  pending: number;
  held: number;
  conflict: number;
  unstarted: number;
}
export interface CanonicalTarget {
  workflow_id: string;
  workflow_version: number;
  workflow_hash: string;
  control_revision: number;
}
export interface CanonicalManifestSource {
  entry_id: string;
  source_sender: string;
  expected_version: number;
  source_hash?: string;
  etag?: string;
  reviewed_token?: string | null;
}
export interface CanonicalManifestDefinition {
  definition_key: string;
  behavior_hash: string;
  graph_hash: string;
  workflow_id: string;
  definition: WorkflowDefinition;
  target: CanonicalTarget;
  allocation: {
    mode: "create" | "reuse_pending" | "reuse_published" | string;
    owner_batch_id: string | null;
    ownership_revision: number | null;
  };
}
export interface CanonicalManifestMapping {
  mapping_id: string;
  ordinal: number;
  operation_id: string;
  binding_action?: "guidance_carry_forward" | "pattern_binding";
  definition_key: string;
  source: CanonicalManifestSource;
  reviewed_origin_hash?: string;
}
export interface CanonicalManifest {
  version: number;
  batch_id: string;
  preparer: CanonicalActor;
  reason: string;
  definitions: CanonicalManifestDefinition[];
  mappings: CanonicalManifestMapping[];
  exceptions: Array<{ ordinal: number; entry_id?: string; source_sender?: string; code: string; field?: string }>;
}
export interface CanonicalItem {
  ordinal: number;
  kind: "mapping" | "exception";
  entry_id: string | null;
  source_sender: string | null;
  operation_id: string;
  workflow_id: string | null;
  binding_action: string | null;
  state: CanonicalItemState;
  reason_code: string | null;
  source_version: number | null;
  child_batch_id: string | null;
  dependency: { ordinal: number; state: string; reason_code: string | null } | null;
  verification?: "matches" | "applied_then_changed" | "pending" | "unavailable" | null;
}
export interface CanonicalDefinitionRow {
  workflow_id: string;
  state: string;
  reason_code: string | null;
  conformance?: string | null;
  verification?: string | null;
  [key: string]: unknown;
}
export interface CanonicalWaiting {
  workflow_id: string;
  ordinal: number;
  owner: {
    batch_id: string;
    manifest_hash: string;
    state: string;
    definition_state: string | null;
    definition_reason_code: string | null;
    links: CanonicalLinks;
  };
  ownership_revision: number;
  blocked: boolean;
  rehome: { takeover: string; new_preview: string; action: string } | null;
}
export interface CanonicalExecutor {
  event_id: string;
  kind: "first_start" | "recovery";
  actor: CanonicalActor;
  reason: string | null;
  recorded_at: string;
}
export interface CanonicalApplicationResult {
  batch_id: string;
  manifest_hash: string;
  state: CanonicalApplicationState;
  reason_code: string | null;
  preparer: CanonicalActor;
  authorization: {
    actor: CanonicalActor;
    reason: string;
    authorized_at: string;
    authorization_hash: string;
  } | null;
  executors?: CanonicalExecutor[];
  superseded_by?: string | null;
  counts: CanonicalCounts;
  items: CanonicalItem[];
  definitions: CanonicalDefinitionRow[];
  waiting: CanonicalWaiting[];
  links: CanonicalLinks;
  verified?: boolean;
}
/** Prepare/inspect add the immutable manifest and the whole request's permission. */
export interface CanonicalApplicationView extends CanonicalApplicationResult {
  manifest: CanonicalManifest;
  required_permission: "draft" | "publish";
  allocations?: Array<Record<string, unknown>>;
}
export interface CanonicalApplicationSummary {
  batch_id: string;
  manifest_hash: string;
  state: string;
  preparer: CanonicalActor;
  authorized: boolean;
  superseded_by: string | null;
  workflow_ids: string[];
  links: CanonicalLinks;
}
export interface CanonicalTakeoverRequest {
  workflow_id: string;
  expected_owner_batch_id: string | null;
  expected_allocation_revision: number;
  successor_batch_id: string;
  successor_manifest_hash: string;
  reason: string;
}
export interface CanonicalTakeoverResult {
  status: "transferred" | "conflict" | "held";
  reason_code: string | null;
  owner_batch_id?: string;
  superseded_batch_id?: string | null;
  evidence?: string[];
  [key: string]: unknown;
}
